import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { Company } from '../types';
import Modal from '../components/Modal';
import CompanyForm from '../components/CompanyForm';

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [includeInactive, setIncludeInactive] = useState(true); // Default to showing all companies

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCompanies(0, 100, includeInactive);
      console.log('Companies API response:', response);
      
      // The API returns an array directly
      if (Array.isArray(response)) {
        setCompanies(response);
      } else {
        console.error('Unexpected response format:', response);
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  
  const handleCreate = () => {
    setEditingCompany(null);
    setShowModal(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this company?')) {
      try {
        await apiService.deleteCompany(id);
        fetchCompanies();
      } catch (error: any) {
        console.error('Error deleting company:', error);
        
        let errorMessage = 'Cannot delete company.';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors[0]?.message || errorMessage;
          } else if (errorData.detail) {
            errorMessage = typeof errorData.detail === 'string' 
              ? errorData.detail 
              : 'Cannot delete company with existing contracts.';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        }
        
        alert(errorMessage);
      }
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      if (editingCompany) {
        await apiService.updateCompany(editingCompany.id, data);
      } else {
        await apiService.createCompany(data);
      }
      setShowModal(false);
      fetchCompanies();
    } catch (error: any) {
      console.error('Error saving company:', error);
      
      // Extract error details from the response
      let errorMessage = 'An error occurred while saving the company.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle the new structured error format
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map((err: any) => 
            `• ${err.message}`
          ).join('\n');
          errorMessage = `Validation Error:\n\n${errorMessages}`;
        }
        // Handle legacy error format
        else if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail.map((err: any) => 
              `• ${err.loc ? err.loc.join(' → ') : 'Field'}: ${err.msg}`
            ).join('\n');
            errorMessage = `Validation Error:\n\n${errorMessages}`;
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        }
        // Handle simple message format
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Invoicing Companies</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9em' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive companies
          </label>
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add Company
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company Number</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Email</th>
                <th>Phone</th>
                <th>VAT Number</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                    <h3 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Companies Found</h3>
                    <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
                      Start by adding your first invoicing company.
                    </p>
                    <button className="btn btn-primary" onClick={handleCreate}>
                      Add First Company
                    </button>
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id}>
                    <td>
                      <strong>{company.name}</strong>
                      {company.legal_form && (
                        <span style={{ color: '#7f8c8d', marginLeft: '5px' }}>
                          ({company.legal_form})
                        </span>
                      )}
                    </td>
                    <td>
                      <strong>{company.company_number || '-'}</strong>
                    </td>
                    <td>
                      {company.contact_name && (
                        <>
                          {company.contact_name}
                          {company.contact_role && (
                            <span style={{ color: '#7f8c8d', display: 'block', fontSize: '0.9em' }}>
                              {company.contact_role}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {company.address_street && (
                        <>
                          {company.address_street}<br />
                          {company.address_postcode} {company.address_city}
                          {company.address_country && (
                            <span style={{ color: '#7f8c8d', display: 'block', fontSize: '0.9em' }}>
                              {company.address_country}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td>{company.email_address}</td>
                    <td>{company.telephone_number}</td>
                    <td>{company.vat_number}</td>
                    <td>
                      <span className={`badge ${company.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEdit(company)}
                        style={{ marginRight: '5px' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(company.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <Modal
          title={editingCompany ? 'Edit Company' : 'Add Company'}
          onClose={() => setShowModal(false)}
        >
          <CompanyForm
            initialData={editingCompany}
            onSubmit={handleSubmit}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default Companies;